import { useAuth } from '../context/AuthContext';
import { usersService } from '../services/supabase/users.service';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useUserFilter } from '../context/UserFilterContext';
import { generateUserInitials } from '../utils/userUtils';

// Type pour les informations d'avatar d'utilisateur
export interface UserAvatarInfo {
  id: string;
  initials?: string;
  color?: string;
  photo_url?: string;
  isLoading?: boolean;
}

/**
 * Hook personnalisé pour gérer le profil utilisateur
 * Ce hook est un wrapper autour de useAuth qui expose uniquement les fonctionnalités liées au profil utilisateur
 * @returns État et fonctions pour gérer le profil utilisateur
 */
export const useUserProfile = () => {
  const { 
    userProfile, 
    agencyName, 
    agencyAddress,
    loading, 
    error, 
    updateUserProfile, 
    clearError 
  } = useAuth();

  // État pour stocker les informations des utilisateurs
  const [usersInfo, setUsersInfo] = useState<Record<string, UserAvatarInfo>>({});
  // Référence pour suivre les requêtes en cours
  const pendingRequests = useRef<Set<string>>(new Set());
  // Utiliser le contexte UserFilterContext pour récupérer selectedUserId
  const { selectedUserId, setSelectedUserId } = useUserFilter();

  // Charger les informations des utilisateurs au chargement du composant
  useEffect(() => {
    const loadUsersInfo = async () => {
      try {
        console.log('🔄 useUserProfile - Chargement initial de tous les utilisateurs');
        const users = await usersService.getAllUsers();
        const userInfoMap: Record<string, UserAvatarInfo> = {};
        
        users.forEach(user => {
          if (!user.id) return; // Ignorer les utilisateurs sans ID
          
          // Utiliser la même fonction que celle utilisée pour les marqueurs sur la carte
          const initials = generateUserInitials(user);
          
          userInfoMap[user.id] = {
            id: user.id,
            initials,
            color: user.color || '#4285F4',
            photo_url: user.photo_url || undefined,
            isLoading: false
          };
        });
        
        console.log(`✅ useUserProfile - ${Object.keys(userInfoMap).length} utilisateurs chargés`);
        setUsersInfo(userInfoMap);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des informations des utilisateurs:', error);
      }
    };
    
    loadUsersInfo();
  }, []);

  // Fonction pour effacer le message de succès (à implémenter dans le composant)
  const clearUpdateSuccess = () => {
    // Cette fonction est fournie pour compatibilité avec l'interface précédente
    // Dans cette implémentation, le succès est géré au niveau du composant
  };

  // Fonction pour récupérer les informations d'un utilisateur par son ID
  const getUserInfo = useCallback((userId: string): UserAvatarInfo | null => {
    if (!userId) {
      console.log('⚠️ useUserProfile - getUserInfo appelé avec un ID vide');
      return null;
    }
    
    // Si l'information est déjà en cache, la retourner
    if (usersInfo[userId] && !usersInfo[userId].isLoading) {
      return usersInfo[userId];
    }
    
    // Si une requête est déjà en cours pour cet utilisateur, retourner le placeholder
    if (pendingRequests.current.has(userId)) {
      console.log('⏳ useUserProfile - Requête déjà en cours pour:', userId);
      return usersInfo[userId] || null;
    }
    
    // Sinon, essayer de la récupérer depuis le service
    console.log('🔎 useUserProfile - Récupération dynamique pour:', userId);
    
    // Marquer comme en cours de chargement
    pendingRequests.current.add(userId);
    
    // Créer un placeholder temporaire avec état de chargement
    const tempInfo: UserAvatarInfo = {
      id: userId,
      initials: '...',
      color: '#cccccc',
      isLoading: true
    };
    
    setUsersInfo(prev => ({
      ...prev,
      [userId]: tempInfo
    }));
    
    // Charger les informations de l'utilisateur de manière asynchrone
    usersService.getUserProfile(userId)
      .then(user => {
        if (user) {
          console.log('✅ useUserProfile - Utilisateur récupéré:', user.id);
          const initials = generateUserInitials(user);
          const userInfo: UserAvatarInfo = {
            id: user.id,
            initials,
            color: user.color || '#4285F4',
            photo_url: user.photo_url || undefined,
            isLoading: false
          };
          
          setUsersInfo(prev => ({
            ...prev,
            [userId]: userInfo
          }));
        } else {
          console.log('⚠️ useUserProfile - Utilisateur non trouvé:', userId);
          setUsersInfo(prev => ({
            ...prev,
            [userId]: {
              ...tempInfo,
              initials: '?',
              isLoading: false
            }
          }));
        }
      })
      .catch(error => {
        console.error('❌ useUserProfile - Erreur lors de la récupération de l\'utilisateur:', error);
        setUsersInfo(prev => ({
          ...prev,
          [userId]: {
            ...tempInfo,
            initials: '!',
            isLoading: false
          }
        }));
      })
      .finally(() => {
        // Supprimer de la liste des requêtes en cours
        pendingRequests.current.delete(userId);
      });
    
    return tempInfo;
  }, [usersInfo]);

  return {
    userProfile,
    agencyName,
    agencyAddress,
    loading,
    error,
    updateSuccess: false, // Géré au niveau du composant
    updateUserProfile,
    clearError,
    clearUpdateSuccess,
    getUserInfo,
    selectedUserId,
    setSelectedUserId
  };
};

